import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, ValidationErrors } from '@angular/forms';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { MUNILY_FROALA_LICENSE_KEY } from './froala-license';
import {
  MunilyTextAreaFieldErrorMessages,
  MunilyTextAreaFieldWidthLayout,
  MunilyTextAreaValueFormat,
} from './text-area-field.types';

/** Toolbar reducida: negrita, cursiva, listas numeradas/viñetas, enlace. */
const DEFAULT_FROALA_TOOLBAR = ['bold', 'italic', '|', 'formatOL', 'formatUL', '|', 'insertLink'] as const;

@Component({
  selector: 'app-text-area-field',
  templateUrl: './text-area-field.component.html',
  styleUrls: ['./text-area-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextAreaFieldComponent implements OnInit, OnChanges, OnDestroy {
  private static _idSeq = 0;

  @Input() control?: FormControl;

  @Input() widthLayout: MunilyTextAreaFieldWidthLayout = 'ds';
  @Input() maxWidth?: string;
  @Input() label = '';
  @Input() placeholder = '';
  @Input() hint = '';
  @Input() disabled = false;
  @Input() successful = false;

  /** Altura mínima del área editable (px), alineada a usos tipo “descripción” en reservas. */
  @Input() minHeightPx = 120;

  /**
   * `html`: el FormControl almacena el HTML de Froala (p. ej. avisos).
   * `plain`: almacena texto plano (saltos de línea preservados); el editor sigue siendo Froala por dentro.
   */
  @Input() valueFormat: MunilyTextAreaValueFormat = 'html';

  /** Contador de caracteres (estilo DS / Figma). Por defecto activo. */
  @Input() hasCharacterCounter = true;

  /**
   * Tope mostrado en el contador (`actual / max`). Por defecto 500; usa `null` para solo mostrar el total sin máximo.
   */
  @Input() maxCharacters: number | null = 500;

  /**
   * Fusión superficial con la configuración interna de Froala (p. ej. eventos).
   * No sustituye `toolbarButtons` salvo que el objeto lo defina explícitamente.
   */
  @Input() froalaOptionsExtra: Record<string, unknown> = {};

  @Input() errorMessage = '';
  @Input() errorMessages: MunilyTextAreaFieldErrorMessages = {};

  @Output() valueChange = new EventEmitter<string>();

  readonly labelId = `munily-ta-label-${TextAreaFieldComponent._idSeq++}`;
  readonly fallbackControl = new FormControl('');

  /** HTML interno para Froala cuando `valueFormat === 'plain'`. */
  readonly froalaHtmlControl = new FormControl('');

  froalaOptions: Record<string, unknown> = {};

  private readonly destroy$ = new Subject<void>();

  /** Evita bucle Froala HTML → plain → HTML cuando el texto plano ya coincide. */
  private syncingPlainFromFroala = false;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  @HostBinding('class.munily-ta-host--ds-default')
  get hostDsDefaultMaxWidth(): boolean {
    return this.widthLayout === 'ds' && !this.maxWidth;
  }

  @HostBinding('style.max-width')
  get hostMaxWidthStyle(): string | null {
    if (this.widthLayout === 'fill') {
      return 'none';
    }
    if (this.widthLayout === 'ds' && this.maxWidth) {
      return this.maxWidth;
    }
    return null;
  }

  get resolvedControl(): FormControl {
    return this.control ?? this.fallbackControl;
  }

  /** Control que enlaza la directiva Froala (HTML en ambos casos). */
  get formControlForFroala(): FormControl {
    return this.valueFormat === 'plain' ? this.froalaHtmlControl : this.resolvedControl;
  }

  /** Caracteres “visibles” para el contador: texto plano (en `html` se deriva del HTML). */
  get displayedCharacterCount(): number {
    const v = this.resolvedControl.value;
    if (v == null) return 0;
    const s = String(v);
    if (this.valueFormat === 'plain') {
      return s.length;
    }
    return this.htmlToPlain(s).length;
  }

  get resolvedErrorMessage(): string {
    if (this.errorMessage) return this.errorMessage;
    const errors = this.resolvedControl.errors;
    if (!errors) return '';
    return this.firstErrorMessage(errors);
  }

  ngOnInit(): void {
    this.seedPlainFroalaFromControl();
    this.rebuildFroalaOptions();
    this.syncDisabled();
    this.wireControlUpdates();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['minHeightPx'] ||
      changes['placeholder'] ||
      changes['froalaOptionsExtra'] ||
      changes['disabled']
    ) {
      this.rebuildFroalaOptions();
    }
    if (changes['disabled']) {
      this.syncDisabled();
    }
    if (changes['control'] && !changes['control'].firstChange) {
      this.destroy$.next();
      this.seedPlainFroalaFromControl();
      this.syncDisabled();
      this.wireControlUpdates();
    }
    if (changes['valueFormat'] && !changes['valueFormat'].firstChange) {
      this.destroy$.next();
      this.seedPlainFroalaFromControl();
      this.syncDisabled();
      this.wireControlUpdates();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showError(): boolean {
    const c = this.resolvedControl;
    return c.invalid && (c.touched || c.dirty);
  }

  showSuccessOutline(): boolean {
    return this.successful && !this.showError();
  }

  /**
   * Angular Froala: si existe `(froalaInit)`, la directiva NO llama a `createEditor()` sola;
   * hay que invocar `controls.initialize()` (igual que en avisos / noticesV2-table).
   */
  onFroalaInit(controls: { initialize: () => void; getEditor: () => unknown }): void {
    controls.initialize();
    this.syncDisabled();
  }

  private seedPlainFroalaFromControl(): void {
    if (this.valueFormat !== 'plain') {
      return;
    }
    this.froalaHtmlControl.setValue(this.plainToHtml(this.resolvedControl.value ?? ''), { emitEvent: false });
  }

  private rebuildFroalaOptions(): void {
    const baseEvents: Record<string, unknown> = {
      'image.beforeUpload': () => false,
      'file.beforeUpload': () => false,
      blur: () => {
        this.resolvedControl.markAsTouched();
        this.cdr.markForCheck();
      },
    };
    const extra = this.froalaOptionsExtra ?? {};
    const extraEvents = (extra['events'] as Record<string, unknown>) || {};
    const base: Record<string, unknown> = {
      key: MUNILY_FROALA_LICENSE_KEY,
      attribution: false,
      toolbarButtons: [...DEFAULT_FROALA_TOOLBAR],
      quickInsertTags: [],
      charCounterCount: false,
      imageUpload: false,
      fileUpload: false,
      videoUpload: false,
      heightMin: this.minHeightPx,
      placeholderText: this.placeholder,
      pasteDeniedTags: ['img'],
      events: { ...baseEvents, ...extraEvents },
    };
    const { events: _e, ...restExtra } = extra;
    this.froalaOptions = { ...base, ...restExtra, events: { ...baseEvents, ...extraEvents } };
    this.cdr.markForCheck();
  }

  private syncDisabled(): void {
    const ctrl = this.resolvedControl;
    if (this.disabled && ctrl.enabled) {
      ctrl.disable({ emitEvent: false });
    } else if (!this.disabled && ctrl.disabled) {
      ctrl.enable({ emitEvent: false });
    }
    if (this.valueFormat === 'plain') {
      if (this.disabled && this.froalaHtmlControl.enabled) {
        this.froalaHtmlControl.disable({ emitEvent: false });
      } else if (!this.disabled && this.froalaHtmlControl.disabled) {
        this.froalaHtmlControl.enable({ emitEvent: false });
      }
    }
  }

  private wireControlUpdates(): void {
    const ctrl = this.resolvedControl;
    ctrl.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.cdr.markForCheck());
    ctrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((v) => {
      this.valueChange.emit(v ?? '');
      this.cdr.markForCheck();
    });

    if (this.valueFormat === 'plain') {
      this.wirePlainValueBridge();
    }
  }

  /** Sincroniza HTML (Froala) ↔ texto plano (FormControl padre). */
  private wirePlainValueBridge(): void {
    this.froalaHtmlControl.valueChanges
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((html) => {
        const plain = this.htmlToPlain(html ?? '');
        const cur = this.resolvedControl.value ?? '';
        if (plain === cur) {
          return;
        }
        this.syncingPlainFromFroala = true;
        this.resolvedControl.setValue(plain, { emitEvent: true });
        this.syncingPlainFromFroala = false;
      });

    this.resolvedControl.valueChanges
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((plain) => {
        if (this.valueFormat !== 'plain' || this.syncingPlainFromFroala) {
          return;
        }
        const html = this.plainToHtml(plain ?? '');
        const cur = this.froalaHtmlControl.value ?? '';
        if (html !== cur) {
          this.froalaHtmlControl.setValue(html, { emitEvent: false });
        }
      });
  }

  private htmlToPlain(html: string): string {
    if (!html || !String(html).trim()) {
      return '';
    }
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent ?? '';
  }

  private plainToHtml(plain: string | null): string {
    if (plain == null || plain === '') {
      return '<p><br></p>';
    }
    const lines = String(plain).split(/\n/);
    return lines.map((line) => `<p>${this.escapeHtml(line)}</p>`).join('');
  }

  private escapeHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  private firstErrorMessage(errors: ValidationErrors): string {
    const key = Object.keys(errors)[0];
    const custom = this.errorMessages[key];
    if (custom) return custom;

    const err = errors[key];
    if (key === 'required') return 'Este campo es obligatorio';
    if (key === 'minlength' && err?.requiredLength != null) return `Mínimo ${err.requiredLength} caracteres`;
    if (key === 'maxlength' && err?.requiredLength != null) return `Máximo ${err.requiredLength} caracteres`;
    if (typeof err === 'object' && err?.message) return String(err.message);
    return 'Valor no válido';
  }
}
